import { useCallback, useEffect, useRef, useState } from 'react';
import { Play, Stop } from '@phosphor-icons/react';
import { useT } from '../i18n';
import { cn } from '../lib/cn';
import { haptic } from '../lib/tma';

/** A public link (a stock sample), or a file that needs our auth header (the clone's own sample). */
export type SampleSource = string | (() => Promise<Blob>);

export interface SamplePlayer {
  /** The key of the sample playing now. */
  playing: string | null;
  toggle(key: string, source: SampleSource): void;
  stop(): void;
}

/**
 * One Audio for the whole page (spec 5.2): ▶ of another sample stops the one
 * playing. Samples are files made once — playing them costs nothing.
 */
export function useSamplePlayer(): SamplePlayer {
  const audio = useRef<HTMLAudioElement | null>(null);
  const blobs = useRef(new Map<string, string>());
  const [playing, setPlaying] = useState<string | null>(null);
  const current = useRef<string | null>(null);

  const stop = useCallback(() => {
    audio.current?.pause();
    current.current = null;
    setPlaying(null);
  }, []);

  useEffect(
    () => () => {
      audio.current?.pause();
      for (const url of blobs.current.values()) URL.revokeObjectURL(url);
    },
    [],
  );

  const play = useCallback((key: string, src: string) => {
    const el = audio.current ?? (audio.current = new Audio());
    el.pause();
    el.onended = () => {
      if (current.current === key) stop();
    };
    el.src = src;
    current.current = key;
    setPlaying(key);
    el.play().catch(() => {
      if (current.current === key) stop();
    });
  }, [stop]);

  const toggle = useCallback(
    (key: string, source: SampleSource) => {
      if (current.current === key) return stop();
      if (typeof source === 'string') return play(key, source);
      const cached = blobs.current.get(key);
      if (cached) return play(key, cached);
      current.current = key;
      setPlaying(key);
      source()
        .then((blob) => {
          const url = URL.createObjectURL(blob);
          blobs.current.set(key, url);
          if (current.current === key) play(key, url);
        })
        .catch(() => {
          if (current.current === key) stop();
        });
    },
    [play, stop],
  );

  return { playing, toggle, stop };
}

/** ▶ / ■ of one sample. */
export function SampleButton({
  player,
  sampleKey,
  source,
  className,
}: {
  player: SamplePlayer;
  sampleKey: string;
  source: SampleSource;
  className?: string;
}) {
  const t = useT();
  const on = player.playing === sampleKey;
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        haptic('light');
        player.toggle(sampleKey, source);
      }}
      aria-label={on ? t.voiceScreen.stopSample : t.voiceScreen.playSample}
      className={cn(
        'w-9 h-9 flex-shrink-0 rounded-full flex items-center justify-center active:scale-95 transition-transform',
        on ? 'bg-ink text-ink-on' : 'bg-surface-2 text-fg',
        className,
      )}
    >
      {on ? <Stop size={14} weight="fill" /> : <Play size={14} weight="fill" />}
    </button>
  );
}
